import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ConsoleLineIcon from 'mdi-react/ConsoleLineIcon';
import StyleConstants from '../../../core/styles.constants';
import { editorTheme } from '../utils/editor-theme';
import type { DrawTarget } from '../models/editor-tool';

interface Props {
  readonly selectedCount: number;
  readonly planSummary: string;
  /**
   * Whether any layer is hidden or faded.
   *
   * Which layers is the rail's business; this only has to say that what is on screen is not
   * the whole plan. An admin who hid the floor layer ten minutes ago has no reason to connect
   * that to the plan now looking like it failed to load, and this is the line that does it.
   */
  /**
   * Where the pointer is on the plan, in the plan's own units, or undefined when it is off
   * the drawing.
   */
  readonly pointerAt?: readonly [number, number];
  readonly layersFiltered?: boolean;
  readonly onShowAllLayers?: () => void;
  readonly onShowCommands?: () => void;
  readonly drawTarget?: DrawTarget;
  readonly onDrawTargetChange?: (target: DrawTarget) => void;
  /** Shown only while a drawing tool is armed, so the bar is not a permanent settings panel. */
  readonly showDrawingOptions?: boolean;
}

const DRAW_TARGETS: ReadonlyArray<{ readonly value: DrawTarget; readonly labelKey: string }> = [
  { value: 'room', labelKey: 'floorplan-editor-layer-rooms' },
  { value: 'floor', labelKey: 'floorplan-editor-layer-floor' },
  { value: 'border', labelKey: 'floorplan-editor-layer-border' },
];

/**
 * Selection summary, the plan's scale, the drawing options in play, and the zoom controls.
 *
 * What it no longer holds is the layer checkboxes: they moved to the rail, where they have
 * room for the opacity and solo controls that belong beside them. What is left here is the
 * line that says the view is filtered at all, which is the part an admin needs when they are
 * looking at the canvas rather than at the panel.
 */
const EditorStatusBar = ({
  selectedCount,
  planSummary,
  pointerAt,
  layersFiltered = false,
  onShowAllLayers,
  onShowCommands,
  drawTarget = 'room',
  onDrawTargetChange,
  showDrawingOptions = false,
}: Props): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        px: 1.5,
        py: 0.5,
        minHeight: 34,
        color: editorTheme.inkDim,
        backgroundColor: editorTheme.surfaceSunken,
        borderTop: `1px solid ${editorTheme.line}`,
        flexWrap: 'wrap',
        // Figures that are read against each other — counts, areas, the scale — line up in
        // columns instead of shuffling sideways every time one of them changes.
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Typography variant="caption" color={StyleConstants.GREY_COLOR} aria-live="polite">
        {t('floorplan-editor-selected-count', { count: selectedCount })}
      </Typography>

      {/* A rule, not a pipe character: a "|" sits on the text baseline and at this size reads
          as a lowercase L in whatever sentence is beside it. */}
      <Box sx={{ width: '1px', alignSelf: 'stretch', my: 0.5, backgroundColor: editorTheme.line }} />

      <Typography variant="caption" color={StyleConstants.GREY_COLOR}>{planSummary}</Typography>

      {/* The pointer's position, which every CAD tool shows and which is the quickest way to
          tell whether a drawing is in millimetres or metres without measuring anything.
          Fixed width, so the rest of the bar does not shuffle sideways as it changes. */}
      {pointerAt && (
        <Typography
          variant="caption"
          sx={{ fontFamily: editorTheme.numeric, color: editorTheme.inkFaint, minWidth: 150 }}
        >
          {`X ${pointerAt[0].toFixed(2)}   Y ${pointerAt[1].toFixed(2)}`}
        </Typography>
      )}

      {/* The scale, and the way to set it. Stated rather than hidden in a panel: it is the one
          fact that decides whether every other number on this screen means anything. */}
      {showDrawingOptions && onDrawTargetChange && (
        <TextField
          select
          size="small"
          value={drawTarget}
          onChange={(event): void => onDrawTargetChange(event.target.value as DrawTarget)}
          label={String(t('floorplan-editor-draw-into'))}
          sx={{ minWidth: 140 }}
        >
          {DRAW_TARGETS.map(({ value, labelKey }) => (
            <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
          ))}
        </TextField>
      )}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, justifyContent: 'flex-end' }}>
        {layersFiltered && onShowAllLayers && (
          <Tooltip title={String(t('floorplan-editor-layers-filtered-hint'))}>
            <Button size="small" onClick={onShowAllLayers} sx={{ textTransform: 'none', py: 0 }}>
              <Typography variant="caption" color={StyleConstants.MEDIUM_BLUE_COLOR}>
                {t('floorplan-editor-layers-filtered')}
              </Typography>
            </Button>
          </Tooltip>
        )}

        {onShowCommands && (
          <Tooltip title={String(t('floorplan-editor-commands-hint'))}>
            <Button
              size="small"
              onClick={onShowCommands}
              startIcon={<ConsoleLineIcon size={14} />}
              sx={{ textTransform: 'none', py: 0 }}
            >
              <Typography variant="caption">{t('floorplan-editor-commands')}</Typography>
            </Button>
          </Tooltip>
        )}
      </Stack>
    </Stack>
  );
};

export default EditorStatusBar;
